<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import WishlistSelectionPopup from '@/components/MyHobbii/views/MyWishlist/modals/WishlistSelectorModal.vue';
import IconHeart from '@/shared/components/UI/Icons/IconHeart.vue';
import TooltipBox from '@/shared/components/UI/TooltipBox.vue';
import TooltipSimple from '@/shared/components/UI/TooltipSimple.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import { openLoginModal } from '@/shared/instances/hobbii-modal';
import { useUserStore } from '@/shared/pinia/modules/user';
import { useWishlistStore } from '@/shared/pinia/modules/wishlist';
import { hobbiiSessionStorage } from '@/shared/utils/web-storage';

type ConfirmedPayload = {
  wishlistIds: string[];
  primaryWishlistName: string;
  totalCount: number;
  willBeInWishlist: boolean;
};

type Props = {
  productGroupIdentifier?: string;
  sku?: string;
  disabled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  productGroupIdentifier: undefined,
  sku: undefined,
  disabled: false
});

const emit = defineEmits<{
  toggle: [
    productGroupIdentifier: string | undefined,
    sku: string | undefined,
    isInWishlist: boolean
  ];
}>();

const { t } = useTranslations();
const wishlistStore = useWishlistStore();
const userStore = useUserStore();

// State for the selection popup
const showSelectionPopup = ref(false);

// Use store state for wishlist status
// The store now fetches ALL wishlists on initial load, so this is always accurate
const isInWishlist = computed(() => {
  return wishlistStore.isInWishlist(props.productGroupIdentifier, props.sku);
});

const isLoading = computed(() => wishlistStore.loading);
const isDataLoaded = computed(() => wishlistStore.dataLoaded);
const isAuthenticated = computed(() => userStore.authenticated);

const showPulse = computed(() => isLoading.value && !isDataLoaded.value);

// Tooltip visibility state
const showTooltip = ref(false);
const TOOLTIP_SESSION_KEY = 'wishlist_tooltip_shown';

// Notification tooltip state (shown when item is added to wishlist)
const showNotificationTooltip = ref(false);
const notificationMessage = ref('');

// Truncate text with ellipsis if it exceeds max length
const MAX_WISHLIST_NAME_LENGTH = 25;
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength)}…`;
};

// Track timeout for cleanup to prevent memory leaks
let notificationTimeoutId: ReturnType<typeof setTimeout> | null = null;

onMounted(() => {
  // Check if tooltip has been shown in this session
  const tooltipShown = hobbiiSessionStorage.getItem(TOOLTIP_SESSION_KEY);
  if (!tooltipShown) {
    showTooltip.value = true;
    hobbiiSessionStorage.setItem(TOOLTIP_SESSION_KEY, 'true');
  }
});

onUnmounted(() => {
  // Clean up notification timeout to prevent memory leaks
  if (notificationTimeoutId !== null) {
    clearTimeout(notificationTimeoutId);
    notificationTimeoutId = null;
  }
});

const handleClick = () => {
  if (props.disabled) {
    return;
  }

  // Hide tooltip when user interacts with the button
  if (showTooltip.value) {
    showTooltip.value = false;
  }

  // Check if user is authenticated, if not show login modal
  if (!isAuthenticated.value) {
    openLoginModal();
    return;
  }

  // Show the wishlist selection popup
  showSelectionPopup.value = true;
};

const handleConfirmed = (payload: ConfirmedPayload) => {
  // Show notification tooltip when items are added
  if (payload.wishlistIds.length > 0) {
    // Build the notification message with truncated wishlist name
    const truncatedName = truncateText(
      payload.primaryWishlistName,
      MAX_WISHLIST_NAME_LENGTH
    );
    const othersCount = payload.totalCount - 1;
    if (othersCount > 0) {
      notificationMessage.value = t(
        'account.wishlist.text_saved_to_and_others',
        {
          wishlistName: truncatedName,
          count: othersCount
        }
      );
    } else {
      notificationMessage.value = `${t('account.wishlist.text_saved_to')} ${truncatedName}`;
    }

    showNotificationTooltip.value = true;
    // Auto-hide after 3 seconds (clear any existing timeout first)
    if (notificationTimeoutId !== null) {
      clearTimeout(notificationTimeoutId);
    }
    notificationTimeoutId = setTimeout(() => {
      showNotificationTooltip.value = false;
      notificationTimeoutId = null;
    }, 3000);
  }

  // Use the pre-calculated state from the payload since store operations
  // execute asynchronously after the confirmed event is emitted
  emit(
    'toggle',
    props.productGroupIdentifier,
    props.sku,
    payload.willBeInWishlist
  );
};
</script>

<template>
  <div class="relative">
    <!-- New wishlist announcement tooltip -->
    <TooltipSimple
      align="center"
      anchor="--tooltip"
      vertical-align="above"
      :time-shown="10000"
      :show="showTooltip"
    >
      {{ t('account.wishlist.badges.text_new') }}
    </TooltipSimple>
    <!-- Notification tooltip for added to wishlist in bottom left corner -->
    <TooltipBox
      :show="showNotificationTooltip"
      :description="notificationMessage"
      arrow-position="none"
      :width="368"
      :height="64"
      :background-color="'#D7F5FA'"
      :center-content="true"
      class="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 md:left-6 md:translate-x-0"
      @hide="showNotificationTooltip = false"
    />
    <button
      :disabled="disabled"
      style="anchor-name: --tooltip"
      class="flex aspect-square size-14 items-center justify-center gap-2 rounded-lg border border-[#A1AAB1] bg-white p-2.5 transition-all duration-200 hover:border-[#00B9CD] hover:shadow-[0_0_0_3px_rgba(0,185,205,0.3)]"
      :class="[
        isInWishlist ? 'text-error-400' : 'text-gray-400 hover:text-[#EE276E]',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      ]"
      :title="
        isInWishlist
          ? t('account.wishlist.text_remove_from_wishlist')
          : t('account.wishlist.text_add_to_wishlist')
      "
      @click="handleClick"
    >
      <IconHeart
        :is-selected="isInWishlist"
        :class="[
          'transition-all duration-200',
          showPulse ? 'animate-pulse' : ''
        ]"
      />
    </button>

    <!-- Wishlist Selection Popup -->
    <WishlistSelectionPopup
      :active="showSelectionPopup"
      :product-group-identifier="productGroupIdentifier"
      :sku="sku"
      @update:active="showSelectionPopup = $event"
      @confirmed="handleConfirmed"
    />
  </div>
</template>
